/**
 * BulkMandateModal — Envoi groupé des formulaires de renseignements fiscaux (Batch #14.291).
 * Début de saison d'impôts : cochez plusieurs clients → chacun reçoit son courriel avec lien magique personnalisé, en un clic.
 */
import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Copy, Loader2, Mail, Search, Users, XCircle } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Field, Modal, Pill, errMsg } from "../comptabilite/_shared";

const STATUS = {
  queued: ["En file", "bg-slate-100 text-slate-600 border-slate-200"], sent: ["Courriel envoyé", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  created: ["Lien créé", "bg-sky-50 text-sky-700 border-sky-200"], created_no_email: ["Lien créé · courriel refusé", "bg-amber-50 text-amber-800 border-amber-200"],
  skipped: ["Déjà envoyé", "bg-slate-100 text-slate-500 border-slate-200"], failed: ["Échec", "bg-rose-50 text-rose-700 border-rose-200"],
};

function Progress({ job }) {
  const processed = (job.items || []).filter((i) => i.status !== "queued").length;
  const pct = job.total ? Math.round((processed / job.total) * 100) : 0;
  const finished = job.status === "done";
  return (
    <div className="space-y-3" data-testid="bulk-progress">
      <div className="flex items-center gap-3">
        {finished ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Loader2 className="w-6 h-6 animate-spin text-rose-600" />}
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800" data-testid="bulk-progress-title">{finished ? "Envoi groupé terminé" : "Envoi en cours…"}</div>
          <div className="text-xs text-slate-500" data-testid="bulk-progress-counts">{job.sent} envoyé{job.sent > 1 ? "s" : ""} · {job.skipped} déjà envoyé{job.skipped > 1 ? "s" : ""} · {job.failed} échec{job.failed > 1 ? "s" : ""} · {job.total} au total</div>
        </div>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-rose-500 transition-all" style={{ width: `${finished ? 100 : Math.min(pct, 99)}%` }} /></div>
      <div className="max-h-72 overflow-y-auto divide-y border rounded-xl" data-testid="bulk-results">
        {(job.items || []).map((it) => {
          const [label, cls] = STATUS[it.status] || STATUS.queued;
          return (
            <div key={it.client_id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`bulk-result-${it.client_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{it.name}</div>
                <div className="text-slate-400 truncate">{it.email || "sans courriel"}{it.error ? ` — ${it.error}` : ""}</div>
              </div>
              <Pill cls={cls} testId={`bulk-result-status-${it.client_id}`}>{label}</Pill>
              {it.magic_link && <button onClick={() => { navigator.clipboard?.writeText(it.magic_link); toast.success("Lien copié"); }} className="p-1 text-slate-400 hover:text-slate-700" title="Copier le lien" data-testid={`bulk-copy-${it.client_id}`}><Copy className="w-3.5 h-3.5" /></button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BulkMandateModal({ onClose, onDone }) {
  const [taxYear, setTaxYear] = useState(new Date().getFullYear() - 1);
  const [prefill, setPrefill] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);
  const [showInPortal, setShowInPortal] = useState(true);
  const [skipExisting, setSkipExisting] = useState(true);
  const [notes, setNotes] = useState("");
  const [candidates, setCandidates] = useState(null);
  const [q, setQ] = useState("");
  const [onlyFiscal, setOnlyFiscal] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);

  useEffect(() => {
    setCandidates(null);
    api.get("/admin/fiscal-mandates/bulk-candidates", { params: { tax_year: taxYear } }).then((r) => setCandidates(r.data.items)).catch((e) => { toast.error(errMsg(e, "Chargement des clients impossible")); setCandidates([]); });
  }, [taxYear]);

  useEffect(() => {
    if (!job || job.status === "done") return;
    const t = setInterval(() => api.get(`/admin/fiscal-mandates/bulk/${job.id}`).then((r) => { setJob(r.data); if (r.data.status === "done") onDone?.(); }).catch(() => {}), 1500);
    return () => clearInterval(t);
  }, [job, onDone]);

  const visible = useMemo(() => (candidates || []).filter((c) => (!onlyFiscal || c.fiscal) && (!q || `${c.name} ${c.email} ${c.master_label}`.toLowerCase().includes(q.toLowerCase()))), [candidates, onlyFiscal, q]);
  const eligible = (c) => (!sendEmail || !!c.email) && !(skipExisting && c.existing);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(visible.filter(eligible).map((c) => c.id)));
  const selectedCount = selected.size;

  const launch = async () => {
    if (!selectedCount) return toast.error("Sélectionnez au moins un client");
    if (!window.confirm(`Envoyer le formulaire de renseignements fiscaux ${taxYear} à ${selectedCount} client${selectedCount > 1 ? "s" : ""} ?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/fiscal-mandates/bulk", { client_ids: [...selected], tax_year: Number(taxYear), prefill, send_email: sendEmail, show_in_portal: showInPortal, notes, skip_existing: skipExisting, origin_url: window.location.origin });
      setJob(r.data);
      if (r.data.status === "done") onDone?.();
    } catch (e) { toast.error(errMsg(e, "Envoi groupé impossible")); }
    finally { setBusy(false); }
  };

  const cb = "w-4 h-4 accent-rose-600";
  return (
    <Modal title="Envoi groupé — formulaires de renseignements fiscaux" sub="Chaque client coché reçoit son propre lien magique (aucune connexion requise)." onClose={onClose} wide testId="bulk-mandate-modal"
      footer={job ? <Btn tone="slate" onClick={onClose} testId="bulk-close-btn">Fermer</Btn> : <>
        <Btn tone="slate" onClick={onClose} testId="bulk-cancel-btn">Annuler</Btn>
        <Btn tone="rose" icon={Mail} onClick={launch} busy={busy} disabled={!selectedCount} testId="bulk-send-btn">Envoyer à {selectedCount} client{selectedCount > 1 ? "s" : ""}</Btn>
      </>}>
      {job ? <Progress job={job} /> : (
        <>
          <div className="grid sm:grid-cols-[110px_1fr] gap-3 items-start">
            <Field label="Année fiscale"><input type="number" value={taxYear} onChange={(e) => setTaxYear(Number(e.target.value) || taxYear)} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" data-testid="bulk-tax-year" /></Field>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-700 pt-5">
              <label className="inline-flex items-center gap-2"><input type="checkbox" checked={prefill} onChange={(e) => setPrefill(e.target.checked)} className={cb} data-testid="bulk-prefill" /> Préremplir avec la fiche client</label>
              <label className="inline-flex items-center gap-2"><input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className={cb} data-testid="bulk-send-email" /> Envoyer par courriel</label>
              <label className="inline-flex items-center gap-2"><input type="checkbox" checked={showInPortal} onChange={(e) => setShowInPortal(e.target.checked)} className={cb} data-testid="bulk-show-portal" /> Afficher au portail</label>
              <label className="inline-flex items-center gap-2"><input type="checkbox" checked={skipExisting} onChange={(e) => setSkipExisting(e.target.checked)} className={cb} data-testid="bulk-skip-existing" /> Ignorer les clients ayant déjà un mandat {taxYear}</label>
            </div>
          </div>
          <Field label="Note interne (facultatif)"><input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" placeholder="Ex. : Envoi de début de saison" data-testid="bulk-notes" /></Field>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client…" className="w-full border border-slate-300 rounded-lg pl-8 pr-3 py-1.5 text-xs" data-testid="bulk-search" />
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={onlyFiscal} onChange={(e) => setOnlyFiscal(e.target.checked)} className={cb} data-testid="bulk-only-fiscal" /> Clients fiscaux seulement</label>
            <button onClick={selectAll} className="text-xs text-rose-700 hover:underline" data-testid="bulk-select-all">Tout sélectionner ({visible.filter(eligible).length})</button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500 hover:underline" data-testid="bulk-select-none">Aucun</button>
            <span className="text-xs text-slate-500 ml-auto" data-testid="bulk-selected-count">{selectedCount} sélectionné{selectedCount > 1 ? "s" : ""}</span>
          </div>

          <div className="border rounded-xl max-h-80 overflow-y-auto divide-y" data-testid="bulk-candidates">
            {candidates === null ? <div className="p-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
              : visible.length === 0 ? <div className="p-6 text-center text-xs text-slate-400"><Users className="w-6 h-6 mx-auto mb-1 opacity-40" /> Aucun client ne correspond.</div>
              : visible.map((c) => {
                const ok = eligible(c);
                return (
                  <label key={c.id} className={`flex items-center gap-3 px-3 py-2 text-xs ${ok ? "hover:bg-slate-50 cursor-pointer" : "opacity-60"}`} data-testid={`bulk-candidate-${c.id}`}>
                    <input type="checkbox" checked={selected.has(c.id)} disabled={!ok} onChange={() => toggle(c.id)} className={cb} data-testid={`bulk-check-${c.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 truncate">{c.name}{c.master_label && <span className="text-slate-400 font-normal"> · {c.master_label}</span>}</div>
                      <div className="text-slate-400 truncate">{c.email || <span className="text-rose-500">Aucun courriel</span>}{c.portal_enabled ? " · portail actif" : ""}</div>
                    </div>
                    {!c.fiscal && <Pill cls="bg-slate-100 text-slate-500 border-slate-200">non fiscal</Pill>}
                    {c.existing && <Pill cls={c.existing.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"} testId={`bulk-existing-${c.id}`}>{c.existing.status === "completed" ? `Signé ${taxYear}` : `Envoyé ${taxYear} · en attente`}</Pill>}
                    {!c.email && sendEmail && <XCircle className="w-4 h-4 text-rose-400" title="Sans courriel : décochez « Envoyer par courriel » pour créer le lien seulement" />}
                  </label>
                );
              })}
          </div>
        </>
      )}
    </Modal>
  );
}
